import { defineMessage } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags-state';
import { PaywallFeature } from '@canvas/paywall-types';

import CloudIconsPaywallHeroDark from './assets/cloud-icons-paywall-hero-dark.webp';
import CloudIconsPaywallHeroLight from './assets/cloud-icons-paywall-hero-light.webp';
import GenericPaywallHeroDark from './assets/generic-paywall-hero-dark.svg';
import GenericPaywallHeroLight from './assets/generic-paywall-hero-light.svg';
import JiraIssueUnfurlPaywallHeroDark from './assets/jira-unfurl-paywall-hero-dark.svg';
import JiraIssueUnfurlPaywallHeroLight from './assets/jira-unfurl-paywall-hero-light.svg';
import { type CTAContent, type CTAUserType } from './paywall-upgrade-service';

type CTAConfig = {
  [feature in PaywallFeature]: CTAContent;
};

/**
 * Use this in the event that you currently waiting for PM and/or design
 * provide a proper content.
 *
 * Note that this content has not been vetted by design. Use it only to unblock
 * yourself from completing the end to end flow.
 *
 * @privateRemarks Please don't delete this variable if it ever becomes unused. This
 * may come in handy for future features where support pages are still
 * being created
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GENERIC_PLACEHOLDER_CONTENT = {
  title: defineMessage({
    id: 'whiteboards.paywall.cta.generic.title',
    defaultMessage: 'Only available on premium',
    description:
      'Header title text explaining that the feature can only be used by premium customers',
  }),
  body: defineMessage({
    id: 'whiteboards.paywall.cta.generic.body',
    defaultMessage: 'This feature is only available for customers on premium',
    description:
      'Body description text that explains that the feature can only be used by premium customers',
  }),
  image: {
    src: {
      default: GenericPaywallHeroLight,
      dark: GenericPaywallHeroDark,
    },
  },
  learnMore: {
    href: 'https://www.atlassian.com/software/confluence/whiteboards',
    message: defineMessage({
      id: 'whiteboards.paywall.cta.generic.learn-more',
      defaultMessage: 'Learn about this feature',
      description: 'Link for learning more about this feature',
    }),
  },
} satisfies CTAContent;

const sharedMessages = {
  smartConnectorsLearnMore: defineMessage({
    id: 'whiteboards.paywall.cta.smart-connectors.learn-more',
    defaultMessage: 'Learn about smart connectors',
    description: 'Link for learning more about smart connectors',
  }),
  smartSectionsLearnMore: defineMessage({
    id: 'whiteboards.paywall.cta.smart-sections.learn-more',
    defaultMessage: 'Learn about smart sections',
    description:
      'A link that takes users to a page where they can learn more about smart sections',
  }),
};

const paywallCTAConfigOld: Partial<CTAConfig> = {
  [PaywallFeature.SMART_CONNECTORS]: {
    title: defineMessage({
      id: 'whiteboards.paywall.cta.smart-connectors.title',
      defaultMessage: 'Connect Jira issues with smart connectors',
      description:
        'Header text introducing smart connectors feature, where uses can connect Jira issues together on our whiteboard',
    }),
    body: defineMessage({
      id: 'whiteboards.paywall.cta.smart-connectors.body',
      defaultMessage:
        'Link Jira issues from whiteboards with smart connectors. Try connecting Jira issues by upgrading your plan.',
      description:
        'Text explaining how smart connectors work, allowing users to link Jira issues directly from a whiteboard',
    }),
    image: {
      src: {
        default: GenericPaywallHeroLight,
        dark: GenericPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/link-jira-issues-from-your-whiteboard',
      message: sharedMessages.smartConnectorsLearnMore,
    },
  },

  [PaywallFeature.SMART_SECTIONS]: {
    title: defineMessage({
      id: 'whiteboards.paywall.cta.smart-sections.title',
      defaultMessage: 'Edit Jira issues with smart sections',
      description:
        'Header text introducing smart sections feature, where users can edit multiple Jira issues within a whiteboard section',
    }),
    body: defineMessage({
      id: 'whiteboards.paywall.cta.smart-sections.body',
      defaultMessage:
        'Bulk edit Jira issues directly from a whiteboard. Try organizing Jira issues with smart sections by upgrading your plan.',
      description:
        'Body text explaining smart sections feature, where users can bulk edit Jira issues directly from a whiteboard',
    }),
    image: {
      src: {
        default: GenericPaywallHeroLight,
        dark: GenericPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/take-bulk-jira-actions-with-smart-sections',
      message: sharedMessages.smartSectionsLearnMore,
    },
  },

  [PaywallFeature.JIRA_ISSUE_UNFURL]: {
    title: defineMessage({
      id: 'whiteboards.paywall.cta.jira-issue-unfurl.title',
      defaultMessage: 'Upgrade to link Jira work items',
      description:
        'Header text introducing Jira issue unfurling feature, where users can import linked work items from a Jira issue into their whiteboard',
    }),
    body: defineMessage({
      id: 'whiteboards.paywall.cta.jira-issue-unfurl.body',
      defaultMessage:
        'Use smart connectors to link Jira work items and visualize their relationships, dependencies, and hierarchies, all from your board.',
      description:
        'Body text explaining Jira issue unfurling feature, where users can import linked work items from a Jira issue into their whiteboard',
    }),
    image: {
      src: {
        default: JiraIssueUnfurlPaywallHeroLight,
        dark: JiraIssueUnfurlPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/link-jira-issues-from-your-whiteboard',
      message: defineMessage({
        id: 'whiteboards.paywall.cta.jira-issue-unfurl.learn-more',
        defaultMessage: 'Learn more',
        description:
          'A link that takes users to a page where they can learn more about importing Jira work items',
      }),
    },
  },
};

const umlShapesCTALearnMoreMessage = defineMessage({
  id: 'whiteboards.paywall.cta.uml-shapes.learn-more',
  defaultMessage: 'Explore UML diagramming shapes',
  description:
    'A link that takes users to a page where they can learn more about UML diagramming shapes',
});

const cloudArchitectureShapesCTALearnMoreMessage = defineMessage({
  id: 'whiteboards.paywall.cta.cloud-architecture-shapes.learn-more',
  defaultMessage: 'Explore cloud architecture icons',
  description:
    'A link that takes users to a page where they can learn more about cloud architecture icons',
});

const cloudArchitectureShapesCTATitleMessage = defineMessage({
  id: 'whiteboards.paywall.cta.cloud-architecture-shapes.title',
  defaultMessage: 'Upgrade to unlock Premium elements',
  description:
    'Header text introducing cloud architecture shapes feature, where users can now diagram using cloud architecture icons. For example icons for AWS, Azure, GCP, etc.',
});

const paywallCTAConfigNew: Partial<CTAConfig> = {
  [PaywallFeature.SMART_CONNECTORS]: {
    title: defineMessage({
      id: 'whiteboards.paywall.cta.smart-connectors.title-issue-terminology-refresh-m4',
      defaultMessage: 'Connect Jira work items with smart connectors',
      description:
        'Header text introducing smart connectors feature, where uses can connect Jira work items together on our whiteboard',
    }),
    body: defineMessage({
      id: 'whiteboards.paywall.cta.smart-connectors.body-issue-terminology-refresh-m4',
      defaultMessage:
        'Link Jira work items from whiteboards with smart connectors. Try connecting Jira work items by upgrading your plan.',
      description:
        'Text explaining how smart connectors work, allowing users to link Jira work items directly from a whiteboard',
    }),
    image: {
      src: {
        default: GenericPaywallHeroLight,
        dark: GenericPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/link-jira-issues-from-your-whiteboard',
      message: sharedMessages.smartConnectorsLearnMore,
    },
  },

  [PaywallFeature.SMART_SECTIONS]: {
    title: defineMessage({
      id: 'whiteboards.paywall.cta.smart-sections.title-issue-terminology-refresh-m4',
      defaultMessage: 'Edit Jira work items with smart sections',
      description:
        'Header text introducing smart sections feature, where users can edit multiple Jira work items within a whiteboard section',
    }),
    body: defineMessage({
      id: 'whiteboards.paywall.cta.smart-sections.body-issue-terminology-refresh-m4',
      defaultMessage:
        'Bulk edit Jira work items directly from a whiteboard. Try organizing Jira work items with smart sections by upgrading your plan.',
      description:
        'Body text explaining smart sections feature, where users can bulk edit Jira work items directly from a whiteboard',
    }),
    image: {
      src: {
        default: GenericPaywallHeroLight,
        dark: GenericPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/take-bulk-jira-actions-with-smart-sections',
      message: sharedMessages.smartSectionsLearnMore,
    },
  },
  [PaywallFeature.CLOUD_ARCHITECTURE_SHAPES]: {
    title: cloudArchitectureShapesCTATitleMessage,
    body: 'cloud-icons-default',
    image: {
      src: {
        default: CloudIconsPaywallHeroLight,
        dark: CloudIconsPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/use-shapes-to-visualize-ideas',
      message: cloudArchitectureShapesCTALearnMoreMessage,
    },
  },
  [PaywallFeature.UML_DIAGRAMMING_SHAPES]: {
    title: cloudArchitectureShapesCTATitleMessage,
    body: 'cloud-icons-default',
    image: {
      src: {
        default: CloudIconsPaywallHeroLight,
        dark: CloudIconsPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/use-shapes-to-visualize-ideas',
      message: umlShapesCTALearnMoreMessage,
    },
  },
  [PaywallFeature.JIRA_ISSUE_UNFURL]:
    paywallCTAConfigOld[PaywallFeature.JIRA_ISSUE_UNFURL],
};

// TODO: Convert the entire CTA config map from 1-1 (1 paywall feature -> 1 config) to a map of objects
// so we can do a 1 to many mapping (1 paywall feature -> userType) and show different content based on their
// permissions
const siteAdminPaywallCTAConfig: Partial<CTAConfig> = {
  [PaywallFeature.CLOUD_ARCHITECTURE_SHAPES]: {
    title: cloudArchitectureShapesCTATitleMessage,
    body: 'cloud-icons-admin',
    image: {
      src: {
        default: CloudIconsPaywallHeroLight,
        dark: CloudIconsPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/use-shapes-to-visualize-ideas',
      message: cloudArchitectureShapesCTALearnMoreMessage,
    },
  },
  [PaywallFeature.UML_DIAGRAMMING_SHAPES]: {
    title: cloudArchitectureShapesCTATitleMessage,
    body: 'cloud-icons-admin',
    image: {
      src: {
        default: CloudIconsPaywallHeroLight,
        dark: CloudIconsPaywallHeroDark,
      },
    },
    learnMore: {
      href: 'https://support.atlassian.com/confluence-cloud/docs/use-shapes-to-visualize-ideas',
      message: umlShapesCTALearnMoreMessage,
    },
  },
};

export const getCTAConfigForFeature = (
  feature: PaywallFeature,
  userType: CTAUserType = 'user',
) => {
  if (userType === 'siteAdmin') {
    return siteAdminPaywallCTAConfig[feature] ?? paywallCTAConfigNew[feature];
  }

  return isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
    ? paywallCTAConfigNew[feature]
    : paywallCTAConfigOld[feature];
};
